import classNames from 'classnames/bind';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';

import styleIdentifiers from './Dropdown.module.scss';

const styles = classNames.bind(styleIdentifiers);

export enum DropdownDirection {
  BOTTOM = 'bottom',
  TOP = 'top',
  LEFT = 'left',
  TOP_LEFT = 'top-left',
  RIGHT = 'right',
  KANBAN = 'kanban',
}

export type DropdownProps = {
  toggleContent?: boolean;
  selectorContent?: React.ReactElement | string;
  className?: string;
  direction?: DropdownDirection;
  selectorClass?: string;
  contentClass?: string;
  hover?: boolean;
  containerStyle?: React.CSSProperties;
};

export const Dropdown = ({
  toggleContent,
  selectorContent,
  className,
  direction = DropdownDirection.BOTTOM,
  selectorClass,
  contentClass,
  children,
  hover,
  containerStyle,
}: PropsWithChildren<DropdownProps>) => {
  const node = useRef<HTMLDivElement>(null);
  const [isDropdownActive, setDropdownActive] = useState<boolean>(false);

  /**
   * Alert if clicked on outside of element
   */
  const handleClickOutside = ({ target }: MouseEvent) => {
    if (isDropdownActive && node && !node.current!.contains(target as any)) {
      setDropdownActive(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleClickOutside);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [isDropdownActive, node]);

  useEffect(() => {
    setDropdownActive(false);
  }, []);

  const toggleClick = (forcedValue: boolean) => {
    setDropdownActive(forcedValue || !isDropdownActive);
  };

  const onAction = () => {
    if (toggleContent) setDropdownActive(!isDropdownActive);
  };

  return (
    <div
      className={styles('Dropdown', className, direction)}
      style={containerStyle}
      onMouseEnter={() => (hover ? toggleClick(true) : null)}
      onMouseLeave={() => (hover ? toggleClick(false) : null)}
      ref={node}
    >
      <div
        className={styles('dropdown-selector', isDropdownActive && 'active', selectorClass)}
        onClick={() => toggleClick(false)}
      >
        <div className={styles('dropdown-selector-content')}>{selectorContent}</div>
        <div
          className={styles('arrow')}
          onMouseEnter={() => {
            if (hover) toggleClick(true);
          }}
        />
      </div>
      <div
        className={styles('dropdown-content', isDropdownActive && 'active', contentClass)}
        onClick={onAction}
      >
        {children}
      </div>
    </div>
  );
};
